// HFP v3 Patient Overview matrix — View A (build instruction section 5.1;
// ticket TD-208).
//
// Rows = domains, columns = Home | School. Collapsed domain rows render a
// segment bar per context (one segment per sub-domain area, coloured by its
// RESOLVED rating, showing the count — colour + count only); expanding shows
// one row per sub-domain with a rating chip per context.
//
// OUTCOMES ONLY in this view: sense chips show the resolved band word — no
// profile names, no provenance markers, and no means anywhere (the hover tip
// may carry the detail; the drill-down lives in the Clinical view). Colour is
// never the only cue: every chip carries its word and every segment its count.
import React, { useMemo, useState } from 'react';
import {
  AREA_WORDS,
  ENV_AREA_WORDS,
  RATING_TOKENS,
  DIFFERS_BADGE_TOKENS,
  DIFFERS_TOOLTIP,
} from '../../constants/hfpV3Tokens';
import crosswalk from '../../schemas/hfpV3/crosswalk.json';

const CONTEXTS = ['home', 'school'];
const SEGMENT_ORDER = ['priority', 'support', 'none', 'strength', 'insufficient', 'notApplicable'];

const domainRows = crosswalk.filter((r) => r.level === 'domain');
const subdomainName = (domain, subdomain) => {
  const row = crosswalk.find(
    (r) => r.level === 'subdomain' && r.domain === domain && r.subdomain === subdomain
  );
  return (row && row.clinicalName) || subdomain;
};

const wordsFor = (isEnv) => (isEnv ? ENV_AREA_WORDS : AREA_WORDS);

// One sub-domain-level area record -> { rating, word } (senses resolve to
// their band; the profile may have raised it, but only the word shows here).
function areaEntries(scoring, domain, context) {
  const ctx = scoring.contexts[context];
  if (domain === 'Sensory') {
    return Object.entries(ctx.senses).map(([sense, record]) => ({
      key: sense,
      name: subdomainName('Sensory', sense),
      rating: record.band,
      // TD-208: OUTCOMES ONLY on the overview — the resolved band word, no
      // profile names (they live in the hover tip and the drill-down).
      word: AREA_WORDS[record.band],
      tip: `${AREA_WORDS[record.band]}${record.label ? ` · profile: ${record.label}` : ''}${
        record.raisedByProfile ? ' (raised by profile)' : ''
      }`,
      differs: !!scoring.differs.sensory[sense],
      differsDir: (scoring.differs.sensory[sense] || {}).worseAt || null,
    }));
  }
  const dom = ctx.functional[domain] || { subdomains: {} };
  const isEnv = domain === 'Environmental Factors';
  return Object.entries(dom.subdomains).map(([sub, record]) => {
    const differsRec = scoring.differs.functional[`${domain}::${sub}`];
    return {
      key: sub,
      name: subdomainName(domain, sub),
      rating: record.rating,
      word: wordsFor(isEnv)[record.rating],
      tip: wordsFor(isEnv)[record.rating],
      differs: !!differsRec,
      differsDir: (differsRec || {}).worseAt || null,
    };
  });
}

function domainDiffers(scoring, domain) {
  if (domain === 'Sensory') return Object.keys(scoring.differs.sensory).length > 0;
  if (domain === 'Environmental Factors') return false;
  return (
    !!scoring.differs.domains[domain] ||
    Object.keys(scoring.differs.functional).some((k) => k.startsWith(`${domain}::`))
  );
}

function SegmentBar({ entries }) {
  // Word from the entries themselves, so environmental rows read the
  // barrier/facilitator words ("Severe barriers"), not the child words.
  const counts = SEGMENT_ORDER.map((rating) => ({
    rating,
    n: entries.filter((e) => e.rating === rating).length,
    word: (entries.find((e) => e.rating === rating) || {}).word || AREA_WORDS[rating],
  })).filter((c) => c.n > 0);
  return (
    <div className="flex gap-0.5 h-6 rounded-md overflow-hidden" role="img"
      aria-label={counts.map((c) => `${c.word}: ${c.n}`).join(', ')}>
      {counts.map((c) => {
        const t = RATING_TOKENS[c.rating];
        return (
          <div
            key={c.rating}
            title={`${c.word}: ${c.n}`}
            className="flex items-center justify-center text-xs font-bold rounded-sm min-w-[26px]"
            style={{ flex: c.n, background: t.bg, color: t.fg, border: `1px solid ${t.border}` }}
          >
            {c.n}
          </div>
        );
      })}
    </div>
  );
}

function DiffersBadge({ direction }) {
  return (
    <span
      title={DIFFERS_TOOLTIP}
      className="inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide"
      style={{
        background: DIFFERS_BADGE_TOKENS.bg,
        color: DIFFERS_BADGE_TOKENS.fg,
        border: `1px solid ${DIFFERS_BADGE_TOKENS.border}`,
      }}
    >
      ⚠ Differs{direction ? ` · worse at ${direction}` : ''}
    </span>
  );
}

function RatingChip({ word, tokens, tip }) {
  return (
    <span
      title={tip}
      className="inline-block rounded-lg px-2.5 py-1 text-xs font-semibold text-center leading-snug"
      style={{ background: tokens.bg, color: tokens.fg, border: `1px solid ${tokens.border}` }}
    >
      {word}
    </span>
  );
}

const LEGEND_PAIRS = [
  ['strength', 'Strength', 'Facilitators in place'],
  ['none', 'No difficulty', 'No barriers'],
  ['support', 'Support need', 'Barriers present'],
  ['priority', 'Priority area', 'Severe barriers'],
  ['insufficient', 'Insufficient data', 'Not applicable'],
];

function Legend() {
  return (
    <div className="grid gap-x-4 gap-y-1 items-center text-[11px] text-gray-600 mb-2"
      style={{ gridTemplateColumns: 'auto repeat(5, minmax(0, auto))' }}>
      <span className="text-[10px] font-bold uppercase tracking-wide text-gray-500">Child</span>
      {LEGEND_PAIRS.map(([rating, child]) => (
        <span key={`c-${rating}`} className="flex items-center gap-1.5">
          <span className="w-3 h-3 rounded flex-none" style={{
            background: RATING_TOKENS[rating].bg,
            border: `1px solid ${RATING_TOKENS[rating].border}`,
          }} />
          {child}
        </span>
      ))}
      <span className="text-[10px] font-bold uppercase tracking-wide text-gray-500">Environment</span>
      {LEGEND_PAIRS.map(([rating, , env]) => (
        <span key={`e-${rating}`} className="flex items-center gap-1.5">
          <span className="w-3 h-3 rounded flex-none" style={{
            background: RATING_TOKENS[rating].bg,
            border: `1px solid ${RATING_TOKENS[rating].border}`,
          }} />
          {env}
        </span>
      ))}
    </div>
  );
}

/**
 * @param {object} scoring   patientRecord.domains.hfpV3.scoring (engine payload)
 * @param {function} onOpenArea  ({ domain, subdomain }) -> void; deep link into
 *                               the Clinical view at the matching area
 */
export default function HfpV3OverviewMatrix({ scoring, onOpenArea }) {
  const [open, setOpen] = useState({});
  const rows = useMemo(
    () =>
      domainRows.map((d) => ({
        domain: d.domain,
        name: d.clinicalName,
        entriesByContext: Object.fromEntries(
          CONTEXTS.map((c) => [c, areaEntries(scoring, d.domain, c)])
        ),
        differs: domainDiffers(scoring, d.domain),
      })),
    [scoring]
  );

  return (
    <div className="text-sm">
      <div className="flex gap-2 mb-3">
        {CONTEXTS.map((c) => (
          <span key={c} className="inline-flex items-center gap-1.5 rounded-full bg-gray-100 px-3 py-1 text-xs font-medium text-gray-600">
            {c === 'home' ? 'Home' : 'School'} data{' '}
            <b className="text-gray-800">{scoring.contexts[c].completeness}% complete</b>
          </span>
        ))}
      </div>
      <Legend />
      <div className="grid items-end border-b-2 border-gray-200 pb-1 mb-0.5"
        style={{ gridTemplateColumns: 'minmax(0,1fr) 132px 132px' }}>
        <span className="text-[10px] font-bold uppercase tracking-wide text-gray-500 pl-1">
          Function domain
        </span>
        <span className="text-xs font-bold text-center text-gray-800">Home</span>
        <span className="text-xs font-bold text-center text-gray-800">School</span>
      </div>
      {rows.map((row) => (
        <div key={row.domain} className="border-b border-gray-100">
          <div className="grid items-center" style={{ gridTemplateColumns: 'minmax(0,1fr) 132px 132px' }}>
            <button
              type="button"
              aria-expanded={!!open[row.domain]}
              aria-controls={`hfpv3-rows-${row.domain.replace(/[^a-zA-Z0-9]/g, '-')}`}
              onClick={() => setOpen((o) => ({ ...o, [row.domain]: !o[row.domain] }))}
              className="flex items-center gap-2 py-2.5 pl-1 pr-2 text-left min-w-0"
            >
              <span aria-hidden="true" className="text-gray-400 text-xs transition-transform"
                style={{ transform: open[row.domain] ? 'rotate(90deg)' : 'none' }}>
                ▶
              </span>
              <span className="font-semibold text-gray-800 truncate">{row.name}</span>
              {row.differs && <DiffersBadge />}
            </button>
            {CONTEXTS.map((c) => (
              <div key={c} className="px-2 py-1.5 border-l border-gray-100">
                <SegmentBar entries={row.entriesByContext[c]} />
              </div>
            ))}
          </div>
          {open[row.domain] && (
            <div id={`hfpv3-rows-${row.domain.replace(/[^a-zA-Z0-9]/g, '-')}`}>
            {row.entriesByContext.home.map((homeEntry) => {
              const schoolEntry =
                row.entriesByContext.school.find((e) => e.key === homeEntry.key) || homeEntry;
              return (
                <div
                  key={homeEntry.key}
                  className="grid items-center border-t border-gray-50 hover:bg-gray-50"
                  style={{ gridTemplateColumns: 'minmax(0,1fr) 132px 132px' }}
                >
                  <button
                    type="button"
                    onClick={() => onOpenArea && onOpenArea({ domain: row.domain, subdomain: homeEntry.key })}
                    title="Open in clinical view"
                    className="flex items-center gap-2 text-left text-gray-600 py-2 pl-7 pr-2 min-w-0"
                  >
                    <span className="truncate">{homeEntry.name}</span>
                    {(homeEntry.differs || schoolEntry.differs) && (
                      <DiffersBadge direction={homeEntry.differsDir || schoolEntry.differsDir} />
                    )}
                  </button>
                  {[homeEntry, schoolEntry].map((entry, j) => (
                    <div key={CONTEXTS[j]} className="px-2 py-1.5 text-center border-l border-gray-50">
                      <RatingChip word={entry.word} tokens={RATING_TOKENS[entry.rating]} tip={entry.tip} />
                    </div>
                  ))}
                </div>
              );
            })}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
